import { useCallback, useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import moment from "moment";
import { Line } from "react-chartjs-2";
import { type ChartJSOrUndefined } from "react-chartjs-2/dist/types";

import { LoadingSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { useAdoptedBudgetMix } from "@features/mmm/hooks/use-adopted-budget-mix";
import { debouncedResize } from "@features/sales-v3/utils/chart";
import {
  formatMetric,
  type MetricPrecision,
} from "@north-beam/nb-common/formatting";
import { TrackPerformanceTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useClientKpis } from "@pages/mmm/hooks/use-client-kpis";
import { useClientSettingData } from "@pages/mmm/hooks/use-client-setting-data";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";

import { usePerformanceTrackingChartMetric } from "../../hooks/use-chart-state";
import { usePerformanceTrackingKpiData } from "../../hooks/use-performance-tracking-kpi-data";
import { usePerformanceTrackingLiveForecastData } from "../../hooks/use-performance-tracking-live-forecast";
import { MetricSelector } from "../metric-selector";

import { ChartViewsButtons } from "./chart-views-buttons";
import {
  ACTUAL_ID,
  LIVE_FORECAST_ID,
  PLANNING_TARGET_ID,
  TARGET_RANGE_ID,
} from "./const";
import { TrackingChartLegend } from "./kpi-chart-legend";
import {
  useBuildKpiDataset,
  useBuildLiveForecastKpiDataset,
} from "./kpi-chart-legend/utils";
import { getTrackingChartOptions } from "./kpi-chart-options";
import { plugins } from "./plugins";
import { setChartData } from "./plugins/chart-data-manager-plugin";

const chartDatasets: any[] = [];

export function KpiChart() {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.TRACK_PERFORMANCE);
  const { user } = useUser();

  const { trackingKpiData, loading } = usePerformanceTrackingKpiData();
  const { trackingliveForecastData } = usePerformanceTrackingLiveForecastData();

  const [datasetVisibility, setDatasetVisibility] = useState<
    Record<string, boolean>
  >({
    [ACTUAL_ID]: true,
    [PLANNING_TARGET_ID]: true,
    [TARGET_RANGE_ID]: true,
    [LIVE_FORECAST_ID]: true,
  });

  const { datasets, dateLabels } = useBuildKpiDataset({
    data: trackingKpiData,
    datasetVisibility,
  });

  const { datasets: forecastDatasets } = useBuildLiveForecastKpiDataset({
    data: trackingliveForecastData,
    datasetVisibility,
  });

  const { adoptedClientSetting } = useClientSettings();
  const { adoptedBudgetMix } = useAdoptedBudgetMix();
  const { kpis: clientKpis } = useClientKpis();
  const { kpi: adoptedClientSettingKpi } =
    useClientSettingData(adoptedClientSetting);

  const kpiName = useMemo(() => {
    let name = adoptedClientSettingKpi?.friendly_name;

    if (!name) {
      name = clientKpis.find(
        (e) => e.kpi_name === adoptedBudgetMix?.data.kpi_name,
      )?.friendly_name;
    }

    return name;
  }, [
    adoptedClientSettingKpi?.friendly_name,
    clientKpis,
    adoptedBudgetMix?.data.kpi_name,
  ]);

  const [chartRef, setChartRef] = useState<ChartJSOrUndefined>();
  useEffect(() => {
    chartRef && debouncedResize(chartRef);
  }, [chartRef]);

  useEffect(() => {
    const allDatasets = [...datasets, ...forecastDatasets];

    setChartData(chartRef, {
      data: {
        datasets: allDatasets,
        labels: dateLabels,
      },
      datasetVisibility,
    });
  }, [chartRef, datasets, datasetVisibility, dateLabels, forecastDatasets]);

  const setChartInstance = useCallback(
    (instance: ChartJSOrUndefined | null) => instance && setChartRef(instance),
    [],
  );

  const [metric] = usePerformanceTrackingChartMetric();
  const { kpiFormat } = useClientSettings();
  const formatValue = useCallback(
    (val: number, precision: MetricPrecision = "exact") => {
      const format = metric === "spend" ? "dollars" : kpiFormat;
      return formatMetric(val, format, {
        precision,
        code: user.currencies.currentCurrency,
      });
    },
    [kpiFormat, metric, user.currencies.currentCurrency],
  );

  const chartOptions = useMemo(() => {
    let kpi = adoptedClientSettingKpi;

    if (!kpi) {
      kpi = clientKpis.find(
        (e) => e.kpi_name === adoptedBudgetMix?.data.kpi_name,
      );
    }

    return getTrackingChartOptions({
      formatter: formatValue,
      kpi,
      timezone: user.timezone,
    });
  }, [
    adoptedClientSettingKpi,
    formatValue,
    user.timezone,
    clientKpis,
    adoptedBudgetMix?.data.kpi_name,
  ]);

  return (
    <>
      {loading && (
        <div className="flex-center h-[516px]">
          <LoadingSlide />
        </div>
      )}
      <div className={clsx(loading && "hidden", "flex flex-col space-y-4")}>
        <div className="flex justify-between">
          <MetricSelector />
          <ChartViewsButtons />
        </div>
        <div className="relative flex items-center justify-between">
          <div className="font-bold text-sm mb-2">
            {kpiName} (cumulative and daily)
          </div>
          <TrackingChartLegend
            datasetVisibility={datasetVisibility}
            setDatasetVisibility={(value) => {
              trackMMMEvent({
                component: TrackPerformanceTrackedComponent.KPI_CHART,
                action: "onChange",
                actionInfo: "Change dataset visibility",
              });

              setDatasetVisibility(value);
            }}
          />
          <div />
        </div>
        <motion.div
          className="h-96"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeIn" }}
        >
          <Line
            id="mmm-kpi-chart"
            ref={setChartInstance}
            options={chartOptions}
            plugins={plugins}
            data={{ datasets: chartDatasets }}
          />
        </motion.div>
      </div>
    </>
  );
}
